import React from 'react';

interface ProgressBarProps {
  className?: string;
  color?: 'rainbow' | string;
  height?: number;
  hideDelay?: number;
  percent: number;
  speed?: number;
  style?: React.CSSProperties;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  className = '',
  color = 'rainbow',
  height = 2,
  hideDelay = 0.4,
  percent,
  speed = 0.4,
  style = {},
}) => {
  const containerStyle: React.CSSProperties = {
    opacity: percent < 100 ? 1 : 0,
    transition: `${speed}s opacity`,
    transitionDelay: `${percent < 100 ? 0 : hideDelay}s`,
  };

  const barStyle: React.CSSProperties = {
    display: 'inline-block',
    position: 'fixed',
    top: 0,
    left: 0,
    width: `${percent}%`,
    maxWidth: '100%',
    height: `${height}px`,
    boxShadow: '1px 1px 1px rgba(0,0,0,0.4)',
    borderRadius: '0 1px 1px 0',
    transition: `${speed}s width, ${speed}s background-color`,
    zIndex: 201,
    ...style,
  };

  if (color === 'rainbow') {
    barStyle.backgroundImage =
      style.backgroundImage ||
      'linear-gradient(to right, #4cd964, #5ac8fa, #007aff, #34aadc, #5856d6, #FF2D55)';
    barStyle.backgroundSize = style.backgroundSize || `100vw ${height}px`;
  } else {
    barStyle.backgroundColor = style.backgroundColor || color;
  }

  return (
    <div className={className} style={containerStyle}>
      <div className={className ? `${className}__bar` : ''} style={barStyle} />
    </div>
  );
};

export const FallbackLoader = ({ className }: { className?: string }) => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (progress < 90) {
      interval = setInterval(() => {
        setProgress((prev: number) => Math.min(prev + Math.random() * 10, 90));
      }, 20);
    }

    return () => {
      clearInterval(interval);
    };
  }, [progress]);

  return <ProgressBar className={className} percent={progress} />;
};
